import { useState, useEffect, useRef } from 'react';
import api, { API_URL, ensureFreshToken, forceVersionCheck } from '../lib/api';
import { toast } from 'sonner';
import BulkEmailPreviewDrawer from './BulkEmailPreviewDrawer';
import ContactBatchPreviewDrawer from './ContactBatchPreviewDrawer';
import ContactUpdatePreviewDrawer from './ContactUpdatePreviewDrawer';
import ComposeEmailDialog from './ComposeEmailDialog';
import BulkEditConfirmDialog from './BulkEditConfirmDialog';
import ConfirmActionCard from './ConfirmActionCard';
import { useNavigate } from 'react-router-dom';
import { useLookup } from '../contexts/LookupContext';
import { Sparkle, X, PaperPlaneTilt, Wrench, ArrowClockwise, Lifebuoy, CheckCircle, Lightning } from '@phosphor-icons/react';

// Friendly live-status labels for each tool Crunchy can run mid-stream.
const TOOL_LABELS = {
  search_contacts: 'Searching your CRM…',
  list_stale_contacts: 'Scanning for stale contacts…',
  count_contacts: 'Counting contacts…',
  get_my_profile: 'Checking your account…',
  get_contact_timeline: 'Reading the contact history…',
  get_my_schedule: 'Checking your calendar…',
  get_contact_details: 'Pulling up the contact card…',
  schedule_activity: 'Booking it in your calendar…',
  get_billing_portal_link: 'Fetching your billing link…',
  draft_bulk_email: 'Drafting the bulk email…',
  create_contacts: 'Checking your CRM & building the batch…',
  update_contacts: 'Checking your CRM & preparing the updates…',
  bulk_edit_contacts: 'Counting the contacts to edit…',
  draft_contact_email: 'Drafting the email…',
  find_duplicates: 'Hunting for duplicates…',
  apply_lookup: 'Filtering your Contact List…',
  save_lookup: 'Saving the lookup…',
  list_saved_lookups: 'Fetching your saved lookups…',
  remember: 'Saving that to memory…',
  forget: 'Updating my memory…',
  get_migration_quote: 'Calculating your migration quote…',
  start_migration: 'Kicking off the migration…',
  check_migration_status: 'Checking migration status…',
  check_affiliate_balance: 'Checking your affiliate balance…',
  list_templates: 'Fetching your templates…',
  create_template: 'Saving the template…',
  update_template: 'Updating the template…',
  delete_template: 'Preparing the delete confirmation…',
  list_groups: 'Fetching your groups…',
  create_group: 'Creating the group…',
  add_to_group: 'Adding contacts to the group…',
  remove_from_group: 'Removing contacts from the group…',
  list_deals: 'Checking your pipeline…',
  create_deal: 'Creating the deal…',
  update_deal: 'Updating the deal…',
  manage_activity: 'Updating your schedule…',
  delete_contacts: 'Counting contacts to delete…',
  merge_contacts: 'Lining up the merge…',
};

/**
 * Floating AI Assistant chat widget. Renders:
 *   - A bottom-right "Ask AI" pill button when collapsed
 *   - A chat drawer when expanded
 *
 * Persists conversation across navigation; loads recent history on open.
 * Cmd+/ (or Ctrl+/) toggles the widget from anywhere.
 */
export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // [{role, content, tool_used?}]
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [workingPings, setWorkingPings] = useState(0); // keepalive pings seen during a long run
  const [workStatus, setWorkStatus] = useState(''); // live "what Crunchy is doing" label
  const [conversationId, setConversationId] = useState(null);
  const [budget, setBudget] = useState(null); // {used, remaining, cap}
  const [loaded, setLoaded] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateMsg, setEscalateMsg] = useState('');
  const [escalateBusy, setEscalateBusy] = useState(false);
  const [escalateDone, setEscalateDone] = useState(false);
  const [escalateErr, setEscalateErr] = useState('');
  const [bulkPreview, setBulkPreview] = useState(null); // {tool, preview:{...}}
  const [contactBatch, setContactBatch] = useState(null); // preview from create_contacts
  const [updateBatch, setUpdateBatch] = useState(null); // preview from update_contacts
  const [emailDraft, setEmailDraft] = useState(null); // preview from draft_contact_email
  const [bulkEdit, setBulkEdit] = useState(null); // preview from bulk_edit_contacts
  const [confirmAction, setConfirmAction] = useState(null); // generic destructive-action preview
  const navigate = useNavigate();
  const { setActiveLookup } = useLookup();
  const [buyBusy, setBuyBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // Draggable position — offset from the bottom-right corner, persisted
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('crunchy_pos'));
      if (saved && Number.isFinite(saved.right) && Number.isFinite(saved.bottom)) return saved;
    } catch { /* ignore */ }
    return { right: 20, bottom: 80 };
  });
  const dragRef = useRef(null);

  const startDrag = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const el = e.currentTarget.closest('[data-testid="ai-assistant-drawer"]') || e.currentTarget;
    const box = el.getBoundingClientRect();
    const maxRight = Math.max(4, window.innerWidth - box.width - 4);
    const maxBottom = Math.max(4, window.innerHeight - box.height - 4);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startRight: pos.right, startBottom: pos.bottom, moved: false };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) d.moved = true;
      setPos({
        right: Math.min(Math.max(d.startRight - dx, 4), maxRight),
        bottom: Math.min(Math.max(d.startBottom - dy, 4), maxBottom),
      });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setPos((p) => {
        try { localStorage.setItem('crunchy_pos', JSON.stringify(p)); } catch { /* ignore */ }
        return p;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Keep the (taller) drawer fully on-screen whenever it opens
  useEffect(() => {
    if (!open) return;
    const w = Math.min(380, window.innerWidth - 32);
    const h = Math.min(560, window.innerHeight - 112);
    setPos((p) => {
      const next = {
        right: Math.min(Math.max(p.right, 4), Math.max(4, window.innerWidth - w - 4)),
        bottom: Math.min(Math.max(p.bottom, 4), Math.max(4, window.innerHeight - h - 4)),
      };
      if (next.right !== p.right || next.bottom !== p.bottom) {
        try { localStorage.setItem('crunchy_pos', JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      }
      return p;
    });
  }, [open]);


  // One-click $15 top-up → Stripe checkout for 200 extra messages
  const buyMoreMessages = async () => {
    if (buyBusy) return;
    setBuyBusy(true);
    try {
      const { data } = await api.post('/billing/topup/start', { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      setBuyBusy(false);
    }
  };

  // Keyboard shortcut: Cmd+/ or Ctrl+/ toggles the widget; Esc closes it
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Stale-bundle guard: the moment Crunchy opens, verify this tab is running
  // the latest deploy. If not, api.js purges caches and reloads BEFORE the
  // user invests in a long prompt (fixes "tool ran but no drawer appeared").
  useEffect(() => {
    if (open) {
      forceVersionCheck();
      // Recover any preview that a dropped stream never delivered
      checkPendingPreview();
    }
    // Safety net: while the drawer is open, sweep for undelivered previews
    // every 20s (covers server-side tool runs that outlive the stream).
    if (!open) return;
    const sweep = setInterval(() => checkPendingPreview(), 20000);
    return () => clearInterval(sweep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Load recent conversation + budget when widget opens for the first time
  useEffect(() => {
    if (!open || loaded) return;
    (async () => {
      try {
        const [convRes, budRes] = await Promise.all([
          api.get('/ai/conversations/recent'),
          api.get('/ai/budget'),
        ]);
        setConversationId(convRes.data.conversation_id);
        setMessages(convRes.data.messages || []);
        setBudget(budRes.data);
        setLoaded(true);
      } catch (err) {
        // Likely unauthenticated — silently leave widget empty
        setLoaded(true);
      }
    })();
  }, [open, loaded]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  // Auto-focus input when opening. Also reset the escalate overlay so reopening
  // the drawer always lands the user back in the chat view (with its close X
  // visible) instead of stale escalate state from a previous session.
  useEffect(() => {
    if (open) {
      setEscalateOpen(false);
      setEscalateDone(false);
      setEscalateErr('');
      if (inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  }, [open]);

  const sendMessage = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || busy) return;
    if (overrideText === undefined) setInput('');
    setBusy(true);
    setWorkingPings(0);
    setWorkStatus('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    // Add an empty assistant message that we'll mutate as tokens stream in
    setMessages((m) => [...m, { role: 'assistant', content: '', streaming: true }]);

    // Drawer delivery independent of the stream: the backend persists every
    // preview the moment it's built, so poll while the request is in flight.
    // Even if an edge proxy kills the SSE connection, the drawer still opens.
    const previewPoll = setInterval(() => { checkPendingPreview({ announce: false }); }, 8000);

    try {
      // Pre-flight: ensure the access token in localStorage isn't stale before
      // firing the raw SSE fetch (which bypasses axios's auto-refresh).
      // Fixes the "Crunchy kicked me to login mid-typing" paper cut.
      const token = (await ensureFreshToken()) || localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/ai/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: text, conversation_id: conversationId }),
      });

      if (!res.ok || !res.body) {
        const errBody = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${errBody.slice(0, 200)}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventName = '';
      let gotDone = false;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE frames are separated by \n\n
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
          eventName = 'message';
          let dataLine = '';
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) eventName = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLine += line.slice(5).trim();
          }
          if (!dataLine) continue;
          let payload;
          try { payload = JSON.parse(dataLine); } catch { continue; }

          if (eventName === 'token' && payload.text) {
            setMessages((m) => {
              const out = [...m];
              const last = out[out.length - 1];
              if (last && last.role === 'assistant') {
                out[out.length - 1] = { ...last, content: (last.content || '') + payload.text };
              }
              return out;
            });
          } else if (eventName === 'meta') {
            if (payload.conversation_id) setConversationId(payload.conversation_id);
            if (payload.tool_used) {
              setMessages((m) => {
                const out = [...m];
                const last = out[out.length - 1];
                if (last && last.role === 'assistant') {
                  out[out.length - 1] = { ...last, tool_used: payload.tool_used };
                }
                return out;
              });
            }
          } else if (eventName === 'preview') {
            // Crunchy returned a UI preview payload — route by tool
            routePreview(payload);
          } else if (eventName === 'status') {
            if (payload.tool) setWorkStatus(TOOL_LABELS[payload.tool] || 'Working on it…');
            else if (payload.stage === 'writing') setWorkStatus('Writing my reply…');
          } else if (eventName === 'ping') {
            setWorkingPings((n) => n + 1);
          } else if (eventName === 'done') {
            gotDone = true;
            if (payload.budget) setBudget(payload.budget);
          } else if (eventName === 'error') {
            throw new Error(payload.error || 'AI stream failed');
          }
        }
      }

      if (!gotDone) {
        // The connection was cut mid-stream (edge proxy timeout). The backend
        // keeps working and persists everything — recover it.
        setMessages((m) => {
          const out = [...m];
          const last = out[out.length - 1];
          if (last && last.role === 'assistant') {
            out[out.length - 1] = {
              ...last, streaming: false,
              content: last.content || '⏳ The connection hiccupped, but I\'m still working on it — fetching the result now…',
            };
          }
          return out;
        });
        recoverLostReply(text);
        return;
      }

      // Mark the final assistant message as no longer streaming
      setMessages((m) => {
        const out = [...m];
        const last = out[out.length - 1];
        if (last && last.role === 'assistant') {
          out[out.length - 1] = { ...last, streaming: false };
        }
        return out;
      });
    } catch (err) {
      const detail = err.message || 'Something went wrong.';
      const midStreamCut = err.name === 'TypeError' || /network|fetch|load failed/i.test(detail);
      if (midStreamCut) {
        // Connection died but the backend keeps working — recover instead of erroring
        setMessages((m) => {
          const out = [...m];
          const last = out[out.length - 1];
          if (last && last.role === 'assistant' && last.streaming) {
            out[out.length - 1] = {
              role: 'assistant', streaming: false,
              content: '⏳ The connection hiccupped, but I\'m still working on it — fetching the result now…',
            };
          }
          return out;
        });
        recoverLostReply(text);
      } else {
        // Replace the (possibly empty) streaming bubble with an error
        setMessages((m) => {
          const out = [...m];
          const last = out[out.length - 1];
          if (last && last.role === 'assistant' && last.streaming) {
            out[out.length - 1] = { role: 'assistant', content: `Hmm, couldn't reach the AI: ${detail}`, error: true };
          } else {
            out.push({ role: 'assistant', content: `Hmm, couldn't reach the AI: ${detail}`, error: true });
          }
          return out;
        });
      }
    } finally {
      clearInterval(previewPoll);
      setBusy(false);
      setWorkStatus('');
      // Final sweep: catch any preview persisted after the stream ended
      // (also fixes "said hey later, drawer never appeared")
      checkPendingPreview({ announce: false });
    }
  };

  const startNewConversation = () => {
    setConversationId(null);
    setMessages([]);
  };

  // ── Durable preview delivery ────────────────────────────────────
  // Previews are persisted server-side; ack on display so a preview lost to a
  // dropped stream (edge proxies kill quiet connections) can be recovered.
  const ackPreview = (id) => {
    if (id) api.post(`/ai/previews/${id}/ack`).catch(() => {});
  };

  const routePreview = (payload) => {
    if (!payload?.tool || !payload?.preview) return false;
    if (payload.tool === 'create_contacts') {
      setContactBatch(payload.preview);
    } else if (payload.tool === 'update_contacts') {
      setUpdateBatch(payload.preview);
    } else if (payload.tool === 'draft_contact_email') {
      setEmailDraft(payload.preview);
    } else if (payload.tool === 'bulk_edit_contacts') {
      setBulkEdit(payload.preview);
    } else if (payload.preview?.kind === 'confirm_action') {
      setConfirmAction(payload.preview);
    } else if (payload.tool === 'apply_lookup') {
      setActiveLookup({
        label: payload.preview.label,
        count: payload.preview.count ?? (payload.preview.ids ? payload.preview.ids.length : null),
        query: payload.preview.query || null,
        ids: payload.preview.ids || null,
      });
      navigate('/contacts');
    } else if (payload.tool === 'draft_bulk_email') {
      setBulkPreview({ tool: payload.tool, preview: payload.preview });
    } else {
      // Unknown preview tool ⇒ this bundle is older than the backend.
      toast.info('SalesCrunch was just updated — refresh the page, then ask Crunchy again.', {
        duration: 12000,
        position: 'top-center',
        action: { label: 'Refresh now', onClick: () => window.location.reload() },
      });
      return false;
    }
    ackPreview(payload.preview_id);
    return true;
  };

  const checkPendingPreview = async ({ announce = true } = {}) => {
    try {
      const { data } = await api.get('/ai/previews/pending');
      if (data?.pending) {
        const opened = routePreview({
          tool: data.pending.tool,
          preview: data.pending.preview,
          preview_id: data.pending.preview_id,
        });
        if (opened && announce) {
          toast.info("Recovered Crunchy's preview — it got lost in transit. Review it now.", {
            duration: 8000, position: 'top-center',
          });
        }
        return opened;
      }
    } catch { /* not logged in / endpoint unreachable — ignore */ }
    return false;
  };

  // The backend keeps working after a dropped connection: it finishes the tool,
  // stores the preview, and persists the reply. Poll both back into the UI.
  const recoverLostReply = (userText) => {
    let tries = 0;
    const poll = async () => {
      tries += 1;
      await checkPendingPreview();
      try {
        const { data } = await api.get('/ai/conversations/recent');
        const msgs = data.messages || [];
        const idx = msgs.map((m) => m.content).lastIndexOf(userText);
        const reply = idx >= 0 ? msgs.slice(idx + 1).find((m) => m.role === 'assistant') : null;
        if (reply && reply.content) {
          setConversationId(data.conversation_id);
          setMessages(msgs);
          return;
        }
      } catch { /* keep polling */ }
      if (tries < 6) setTimeout(poll, 8000);
      else {
        setMessages((m) => {
          const out = [...m];
          const last = out[out.length - 1];
          if (last && last.role === 'assistant' && !last.content) {
            out[out.length - 1] = {
              role: 'assistant', error: true,
              content: "The connection dropped and I couldn't fetch the result. Hit ↻ to reload the conversation — my reply is probably waiting there.",
            };
          }
          return out;
        });
      }
    };
    setTimeout(poll, 4000);
  };

  return (
    <>
      {/* Floating "Ask AI" button (collapsed state) */}
      {!open && (
        <button
          onClick={() => { if (dragRef.current?.moved) { dragRef.current.moved = false; return; } setOpen(true); }}
          onPointerDown={startDrag}
          data-testid="ai-assistant-open"
          style={{ right: pos.right, bottom: pos.bottom }}
          className="fixed z-50 bg-gradient-to-r from-[#0B5394] to-[#002FA7] hover:from-[#094180] hover:to-[#00237D] text-white rounded-full shadow-lg hover:shadow-xl transition-all px-5 py-3 flex items-center gap-2 font-semibold group touch-none cursor-grab active:cursor-grabbing"
          title="Ask Crunchy (Cmd+/) — drag to move"
        >
          <Sparkle size={18} weight="fill" className="text-[#F39C12] group-hover:rotate-12 transition-transform" />
          <span>Ask Crunchy</span>
        </button>
      )}

      {/* Chat drawer (expanded state) */}
      {open && (
        <div
          style={{ right: pos.right, bottom: pos.bottom }}
          className="fixed z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-7rem)] bg-white rounded-lg shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          data-testid="ai-assistant-drawer"
        >
          {/* Header (drag handle) */}
          <div
            onPointerDown={startDrag}
            title="Drag to move"
            className="bg-gradient-to-r from-[#0B5394] to-[#002FA7] text-white px-4 py-3 flex items-center gap-2 shrink-0 touch-none cursor-grab active:cursor-grabbing select-none"
          >
            <Sparkle size={18} weight="fill" className="text-[#F39C12]" />
            <div className="flex-1">
              <div className="font-bold text-sm leading-tight">Crunchy</div>
              <div className={`text-[10px] leading-tight ${budget && budget.remaining <= 10 ? 'text-red-300 font-bold' : budget && budget.remaining <= 30 ? 'text-amber-300 font-bold' : 'text-white/70'}`} data-testid="ai-budget-counter">
                {budget
                  ? `${budget.remaining}/${budget.cap} messages left${budget.bonus ? ` (incl. ${budget.bonus} bonus)` : ''}`
                  : 'Your CRM concierge'}
              </div>
            </div>
            {budget && budget.remaining <= 30 && (
              <button
                onClick={buyMoreMessages}
                disabled={buyBusy}
                className="bg-[#F39C12] hover:bg-[#E08C00] disabled:opacity-60 text-black text-[10px] font-bold rounded px-2 py-1 flex items-center gap-1 whitespace-nowrap"
                title="200 extra messages — $15 one-time, never expire"
                data-testid="ai-buy-more-btn"
              >
                <Lightning size={11} weight="fill" />
                {buyBusy ? 'Opening…' : 'Buy more · $15'}
              </button>
            )}
            <button
              onClick={startNewConversation}
              className="text-white/70 hover:text-white p-1 -m-1"
              title="Start new conversation"
              data-testid="ai-assistant-new-conv"
            >
              <ArrowClockwise size={16} weight="bold" />
            </button>
            <button
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white p-1 -m-1"
              title="Close (Cmd+/)"
              data-testid="ai-assistant-close"
            >
              <X size={18} weight="bold" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 && !busy && (
              <div className="text-slate-600 text-sm py-2" data-testid="ai-welcome">
                <div className="text-center mb-4">
                  <Sparkle size={32} weight="fill" className="text-[#F39C12] mx-auto mb-2" />
                  <p className="font-bold text-slate-800 text-base mb-1">Hey, I&apos;m Crunchy 👋</p>
                  <p className="text-xs text-slate-500 leading-relaxed max-w-[280px] mx-auto">
                    I can find contacts, schedule activities, and answer questions about how SalesCrunch works.
                    Tap one to try me:
                  </p>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider px-1 mb-1.5">
                      🔍 Find things
                    </p>
                    <div className="space-y-1.5">
                      {[
                        'How many contacts do I have?',
                        "Who haven't I called in 60 days?",
                        'Find anyone named Smith',
                      ].map((s) => (
                        <button
                          key={s}
                          onClick={() => sendMessage(s)}
                          className="block w-full text-left px-3 py-2 rounded-md bg-white border border-slate-200 hover:border-[#0B5394] hover:bg-blue-50 text-slate-700 text-xs transition-colors"
                          data-testid={`ai-suggestion-find-${s.slice(0, 10).replace(/\s/g, '-')}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider px-1 mb-1.5">
                      💡 How do I…?
                    </p>
                    <div className="space-y-1.5">
                      {[
                        'How do I print mailing labels?',
                        'How do I import from ACT!?',
                        'How do I cancel my subscription?',
                      ].map((s) => (
                        <button
                          key={s}
                          onClick={() => sendMessage(s)}
                          className="block w-full text-left px-3 py-2 rounded-md bg-white border border-slate-200 hover:border-[#F39C12] hover:bg-amber-50 text-slate-700 text-xs transition-colors"
                          data-testid={`ai-suggestion-howto-${s.slice(0, 10).replace(/\s/g, '-')}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <p className="text-[10px] text-slate-400 italic text-center pt-1">
                    …or just type your own question below 👇
                  </p>
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <Message key={i} message={m} />
            ))}

            {/* Live working indicator — visible for the ENTIRE run so long
                tool rounds never look frozen. Shows what Crunchy is doing. */}
            {busy && (
              <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="ai-thinking">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-[#0B5394] rounded-full animate-bounce" />
                  <span className="w-1.5 h-1.5 bg-[#0B5394] rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                  <span className="w-1.5 h-1.5 bg-[#0B5394] rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                </div>
                <span data-testid="ai-working-status">
                  {workStatus
                    ? `${workStatus}${workingPings > 2 ? ' (big one — hang tight)' : ''}`
                    : (workingPings > 0 ? 'Crunching a big one — hang tight, still working…' : 'Thinking…')}
                </span>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-2.5 bg-white shrink-0">
            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="flex gap-1.5 items-end"
            >
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Ask Crunchy anything…"
                disabled={busy}
                rows={1}
                className="flex-1 resize-none border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5394] focus:border-transparent disabled:bg-slate-50 max-h-32"
                data-testid="ai-assistant-input"
                style={{ minHeight: '36px' }}
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="bg-[#0B5394] hover:bg-[#094180] disabled:bg-slate-300 text-white rounded p-2 transition-colors shrink-0"
                data-testid="ai-assistant-send"
                title="Send (Enter)"
              >
                <PaperPlaneTilt size={16} weight="fill" />
              </button>
            </form>
            <div className="flex items-center justify-between mt-1.5 px-1">
              <p className="text-[10px] text-slate-400">Cmd+/ to toggle &middot; Powered by Claude</p>
              <button
                onClick={() => { setEscalateOpen(true); setEscalateDone(false); setEscalateErr(''); setEscalateMsg(''); }}
                className="text-[10px] text-slate-500 hover:text-[#F39C12] font-semibold inline-flex items-center gap-1"
                data-testid="ai-escalate-trigger"
                title="Get help from our support team"
              >
                <Lifebuoy size={10} weight="bold" /> Need a human?
              </button>
            </div>
          </div>

          {/* Escalate to support overlay — appears within the drawer */}
          {escalateOpen && (
            <div className="absolute inset-0 bg-white/97 backdrop-blur-sm flex flex-col p-5 z-10" data-testid="ai-escalate-panel">
              {escalateDone ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center">
                  <CheckCircle size={48} weight="fill" className="text-emerald-500 mb-3" />
                  <h3 className="text-lg font-black text-slate-900 mb-2">Got it — we&apos;re on it!</h3>
                  <p className="text-sm text-slate-600 mb-1 max-w-[280px]">
                    Our support team will email you back within <strong>1 business day</strong>.
                  </p>
                  <p className="text-xs text-slate-400 mt-3">You can keep chatting with me in the meantime 👋</p>
                  <button
                    onClick={() => { setEscalateOpen(false); setEscalateDone(false); }}
                    className="mt-6 bg-[#0B5394] hover:bg-[#094180] text-white text-sm font-bold px-5 py-2 rounded"
                    data-testid="ai-escalate-back"
                  >
                    Back to chat
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-black text-slate-900 text-base inline-flex items-center gap-2">
                      <Lifebuoy size={18} weight="fill" className="text-[#F39C12]" />
                      Need a human?
                    </h3>
                    <button
                      onClick={() => setEscalateOpen(false)}
                      className="text-slate-400 hover:text-slate-700"
                      data-testid="ai-escalate-close"
                    >
                      <X size={18} weight="bold" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-600 mb-3 leading-relaxed">
                    Tell us what you need help with. We typically respond within 1 business day.
                    Crunchy will share your recent conversation with the team so you don&apos;t have to repeat yourself.
                  </p>
                  <textarea
                    value={escalateMsg}
                    onChange={(e) => setEscalateMsg(e.target.value)}
                    placeholder="What can support help you with?"
                    rows={6}
                    maxLength={4000}
                    disabled={escalateBusy}
                    className="border border-slate-300 rounded p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F39C12] resize-none flex-1"
                    data-testid="ai-escalate-textarea"
                  />
                  <p className="text-[10px] text-slate-400 text-right mt-1">{escalateMsg.length} / 4000</p>
                  {escalateErr && (
                    <div className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5 mt-2" data-testid="ai-escalate-err">
                      {escalateErr}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 mt-3">
                    <button
                      onClick={() => setEscalateOpen(false)}
                      disabled={escalateBusy}
                      className="text-xs text-slate-600 hover:text-slate-900 font-semibold px-3 py-1.5"
                      data-testid="ai-escalate-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        const trimmed = escalateMsg.trim();
                        if (trimmed.length < 5) {
                          setEscalateErr('Please give us a bit more detail (at least 5 characters).');
                          return;
                        }
                        setEscalateBusy(true);
                        setEscalateErr('');
                        try {
                          await api.post('/support/escalate', { message: trimmed, conversation_id: conversationId });
                          setEscalateDone(true);
                        } catch (err) {
                          setEscalateErr(err.response?.data?.detail || 'Could not send. Please email chip@migrationcrunch.com directly.');
                        } finally {
                          setEscalateBusy(false);
                        }
                      }}
                      disabled={escalateBusy || escalateMsg.trim().length < 5}
                      className="bg-[#F39C12] hover:bg-[#E08C00] disabled:opacity-50 text-black font-bold text-sm px-4 py-2 rounded inline-flex items-center gap-2"
                      data-testid="ai-escalate-send"
                    >
                      {escalateBusy ? 'Sending…' : <>Send to support <PaperPlaneTilt size={12} weight="fill" /></>}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Bulk-email preview drawer — opens when Crunchy fires draft_bulk_email */}
      <BulkEmailPreviewDrawer
        open={!!bulkPreview}
        onClose={() => setBulkPreview(null)}
        preview={bulkPreview?.preview}
      />

      {/* Contact batch approval drawer — opens when Crunchy fires create_contacts */}
      <ContactBatchPreviewDrawer
        open={!!contactBatch}
        onClose={() => setContactBatch(null)}
        preview={contactBatch}
      />

      {/* Contact update approval drawer — opens when Crunchy fires update_contacts */}
      <ContactUpdatePreviewDrawer
        open={!!updateBatch}
        onClose={() => setUpdateBatch(null)}
        preview={updateBatch}
      />

      {/* One-on-one compose window — opens when Crunchy fires draft_contact_email */}
      <ComposeEmailDialog
        open={!!emailDraft}
        contact={emailDraft?.contact}
        initialSubject={emailDraft?.subject || ''}
        initialBody={emailDraft?.body || ''}
        onClose={() => setEmailDraft(null)}
      />

      {/* Bulk edit confirmation card — opens when Crunchy fires bulk_edit_contacts */}
      <BulkEditConfirmDialog
        open={!!bulkEdit}
        preview={bulkEdit}
        onClose={() => setBulkEdit(null)}
      />

      {/* Generic destructive-action confirm (delete contacts/template, merge) */}
      <ConfirmActionCard
        open={!!confirmAction}
        preview={confirmAction}
        onClose={() => setConfirmAction(null)}
      />
    </>
  );
}

function renderInline(text) {
  return String(text).split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : p
  );
}

function Message({ message }) {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={`ai-msg-${message.role}`}>
      <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
        isUser
          ? 'bg-[#0B5394] text-white'
          : message.error
            ? 'bg-red-50 border border-red-200 text-red-800'
            : 'bg-white border border-slate-200 text-slate-800 shadow-sm'
      }`}>
        {message.tool_used && !isUser && (
          <div className="flex items-center gap-1 text-[10px] text-slate-500 mb-1.5 font-semibold uppercase tracking-wide">
            <Wrench size={10} weight="bold" />
            {message.tool_used.replace(/_/g, ' ')}
          </div>
        )}
        {isUser ? message.content : renderInline(message.content)}
        {message.streaming && (
          <span
            className="inline-block w-1.5 h-3 ml-0.5 align-middle bg-slate-500 animate-pulse"
            data-testid="streaming-cursor"
          />
        )}
      </div>
    </div>
  );
}
